import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Navbar({ onContactClick }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [dynamicNavPages, setDynamicNavPages] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchCompanyInfo();
    fetchDynamicNavPages();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/company-info`);
      // Add cache busting to logo URL
      const data = response.data;
      if (data.logo_url && !data.logo_url.includes('?v=')) {
        data.logo_url = `${data.logo_url}?v=${Date.now()}`;
      }
      setCompanyInfo(data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchDynamicNavPages = async () => {
    try {
      const response = await axios.get(`${API}/dynamic-pages/nav`);
      setDynamicNavPages(response.data);
    } catch (error) {
      console.error('Error fetching dynamic nav pages:', error);
    }
  };

  const staticNavLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/courses', label: 'Courses' },
    { path: '/why-choose-us', label: 'Why Choose Us' },
    { path: '#contact', label: 'Contact', isModal: true },
  ];

  // Combine static and dynamic nav links
  const navLinks = [
    ...staticNavLinks,
    ...dynamicNavPages.map(page => ({
      path: `/page/${page.slug}`,
      label: page.title
    }))
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-md shadow-lg'
          : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center space-x-3">
            {companyInfo?.logo_url ? (
              <img 
                src={companyInfo.logo_url} 
                alt={companyInfo.company_name}
                className="h-16 w-auto object-contain"
              />
            ) : (
              <>
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-sky-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold text-xl">S</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-900 font-bold text-lg leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {companyInfo?.company_name.split(' ')[0] || 'SKANDA IT'}
                  </span>
                  <span className="text-blue-600 text-xs font-semibold tracking-wider">
                    SOLUTIONS
                  </span>
                </div>
              </>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              link.isModal ? (
                <button
                  key={link.label}
                  onClick={() => onContactClick && onContactClick()}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-4 py-2 rounded-sm font-medium transition-all duration-200 text-slate-600 hover:text-blue-600 hover:bg-slate-50"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-sm font-medium transition-all duration-200 ${
                    isActive(link.path)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-slate-600 hover:text-blue-600 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button - also triggers contact modal */}
          <div className="hidden md:block">
            <button
              onClick={() => onContactClick && onContactClick()}
              data-testid="navbar-cta-button"
              className="bg-slate-900 text-white px-6 py-2.5 rounded-sm font-semibold hover:bg-slate-800 transition-all duration-300 hover:shadow-lg"
            >
              Get a Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-slate-200">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <button
                data-testid="mobile-cta-button"
                className="w-full bg-slate-900 text-white px-6 py-3 rounded-sm font-semibold mt-4"
              >
                Get a Quote
              </button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}